import React, { useState, useEffect } from 'react';
import { Modal, Card, Descriptions, Table, Button, Alert, Space, Typography, Divider, Checkbox } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;

const PurchaseConfirmation = ({ 
  visible, 
  onConfirm, 
  onCancel, 
  purchaseData, 
  newSuppliers = [], 
  newProducts = [],
  newPublishers = [],
  newCurrencies = [],
  loading = false 
}) => {
  const [confirmNewSupplier, setConfirmNewSupplier] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Initialize selected products when modal opens
  useEffect(() => {
    if (visible && newProducts.length > 0) {
      // By default, select all new products
      setSelectedProducts(newProducts.map((_, index) => index));
    }
  }, [visible, newProducts]);

  const handleProductSelection = (productIndex, checked) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, productIndex]);
    } else {
      setSelectedProducts(selectedProducts.filter(index => index !== productIndex));
    }
  };

  const handleSelectAllProducts = (checked) => {
    if (checked) {
      setSelectedProducts(newProducts.map((_, index) => index));
    } else {
      setSelectedProducts([]);
    }
  };

  const productColumns = [
    {
      title: (
        <Checkbox 
          checked={selectedProducts.length === newProducts.length && newProducts.length > 0}
          indeterminate={selectedProducts.length > 0 && selectedProducts.length < newProducts.length}
          onChange={(e) => handleSelectAllProducts(e.target.checked)}
        >
          Select
        </Checkbox>
      ),
      dataIndex: 'select',
      key: 'select',
      width: 80,
      render: (_, record, index) => (
        <Checkbox 
          checked={selectedProducts.includes(index)}
          onChange={(e) => handleProductSelection(index, e.target.checked)}
        />
      )
    },
    {
      title: 'ISBN',
      dataIndex: 'isbn',
      key: 'isbn',
      render: (isbn) => isbn || 'N/A'
    },
    {
      title: 'Title',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => name || record.title || 'N/A'
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      render: (author) => author || 'N/A'
    },
    {
      title: 'Publisher',
      dataIndex: 'publisher',
      key: 'publisher',
      render: (publisher, record) => {
        if (publisher) return publisher;
        if (record.book_publisher?.name) return record.book_publisher.name;
        return 'N/A';
      }
    },
    {
      title: 'Quantity',
      dataIndex: 'selectedQty',
      key: 'quantity',
      render: (qty, record) => qty || record.quantity || record.extractedQuantity || 0
    },
    {
      title: 'List (MRP)',
      dataIndex: 'sale_price',
      key: 'price',
      render: (salePrice, record) => {
        const displayPrice =
          salePrice ||
          record.sale_price ||
          record.purchase_price ||
          record.price ||
          record.extractedPrice ||
          0;
        return `₹${displayPrice}`;
      }
    },
    {
      title: 'Discount %',
      dataIndex: 'purchase_disc',
      key: 'discount',
      render: (disc, record) => {
        const displayDisc = disc || record.discount || record.extractedDiscount || 0;
        return `${displayDisc}%`;
      }
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, record) => {
        const qty = record.selectedQty || record.quantity || record.extractedQuantity || 0;
        const price =
          record.sale_price ||
          record.purchase_price ||
          record.price ||
          record.extractedPrice ||
          0;
        const discount = record.purchase_disc || record.discount || record.extractedDiscount || 0;
        const total = qty * price * (1 - discount / 100);
        return `₹${total.toFixed(2)}`;
      }
    }
  ];

  const calculateTotals = () => {
    if (!purchaseData?.selectedProducts) return { subtotal: 0, discount: 0, total: 0 };
    
    let subtotal = 0;
    purchaseData.selectedProducts.forEach(product => {
      const qty = product.selectedQty || product.quantity || product.extractedQuantity || 0;
      const price =
        product.sale_price ||
        product.purchase_price ||
        product.price ||
        product.extractedPrice ||
        0;
      subtotal += qty * price;
    });
    
    const discount = purchaseData.discount || 0;
    const total = subtotal - discount;
    
    return { subtotal, discount, total };
  };

  const totals = calculateTotals();

  const handleConfirm = () => {
    const selectedNewProducts = selectedProducts.map(index => newProducts[index]);
    
    onConfirm({
      ...purchaseData,
      confirmNewSupplier,
      newSuppliers: confirmNewSupplier ? newSuppliers : [],
      newProducts: selectedNewProducts,
      selectedProductIndices: selectedProducts
    });
  };

  return (
    <Modal
      title="Confirm Purchase Bill"
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="confirm" type="primary" loading={loading} onClick={handleConfirm}>
          Confirm & Save Purchase Bill
        </Button>
      ]}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* New Supplier Section */}
        {newSuppliers.length > 0 && (
          <Card 
            title={
              <Space>
                <Checkbox 
                  checked={confirmNewSupplier}
                  onChange={(e) => setConfirmNewSupplier(e.target.checked)}
                >
                  Add New Supplier
                </Checkbox>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            {newSuppliers.map((supplier, index) => (
              <div key={index}>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Name">{supplier.name}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{supplier.phone || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>{supplier.address || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </div>
            ))}
          </Card>
        )}

        {/* New Products Section */}
        {newProducts.length > 0 && (
          <Card 
            title={
              <Space>
                <Text strong>New Products to Add</Text>
                <Text type="secondary">({selectedProducts.length} of {newProducts.length} selected)</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Table
              columns={productColumns}
              dataSource={newProducts}
              pagination={false}
              size="small"
              rowKey={(record, index) => index}
            />
          </Card>
        )}

        {/* New Publishers and Currencies Info */}
        {(newPublishers.length > 0 || newCurrencies.length > 0) && (
          <Alert
            message="Additional Items to be Created"
            description={
              <div>
                {newPublishers.length > 0 && (
                  <Text>📚 {newPublishers.length} new publisher(s): {newPublishers.map(p => p.name).join(', ')}</Text>
                )}
                {newPublishers.length > 0 && newCurrencies.length > 0 && <br />}
                {newCurrencies.length > 0 && (
                  <Text>💱 {newCurrencies.length} new currency(ies): {newCurrencies.map(c => c.name).join(', ')}</Text>
                )}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Summary Alert */}
        {(newSuppliers.length > 0 || newProducts.length > 0) && (
          <Alert
            message="Confirmation Summary"
            description={
              <div>
                {confirmNewSupplier && newSuppliers.length > 0 && (
                  <Text>✓ {newSuppliers.length} new supplier(s) will be added</Text>
                )}
                {confirmNewSupplier && newSuppliers.length > 0 && selectedProducts.length > 0 && <br />}
                {selectedProducts.length > 0 && (
                  <Text>✓ {selectedProducts.length} new product(s) will be added</Text>
                )}
                {newPublishers.length > 0 && <br />}
                {newPublishers.length > 0 && (
                  <Text>✓ {newPublishers.length} new publisher(s) will be created automatically</Text>
                )}
                {newCurrencies.length > 0 && <br />}
                {newCurrencies.length > 0 && (
                  <Text>✓ {newCurrencies.length} new currency(ies) will be created automatically</Text>
                )}
                {!confirmNewSupplier && newSuppliers.length > 0 && (
                  <Text type="warning">⚠ New supplier will be skipped</Text>
                )}
                {!confirmNewSupplier && newSuppliers.length > 0 && selectedProducts.length === 0 && <br />}
                {selectedProducts.length === 0 && newProducts.length > 0 && (
                  <Text type="warning">⚠ No products selected</Text>
                )}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Supplier Information */}
        <Card title="Supplier Information" size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Name">
              {purchaseData?.supplier?.name || 'Not specified'}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {purchaseData?.supplier?.phone || 'Not specified'}
            </Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>
              {purchaseData?.supplier?.address || 'Not specified'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Bill Information */}
        <Card title="Bill Information" size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={3} size="small">
            <Descriptions.Item label="Bill Date">
              {purchaseData?.date ? moment(purchaseData.date).format('DD/MM/YYYY') : 'Not specified'}
            </Descriptions.Item>
            <Descriptions.Item label="Bill Number">
              {purchaseData?.supplier_memo_no || 'Not specified'}
            </Descriptions.Item>
            <Descriptions.Item label="Payment Status">
              {purchaseData?.paid_amount >= totals.total ? 'Fully Paid' : 'Partially Paid'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Products */}
        <Card title={`Products (${purchaseData?.selectedProducts?.length || 0})`} size="small" style={{ marginBottom: 16 }}>
          <Table
            columns={productColumns}
            dataSource={purchaseData?.selectedProducts || []}
            pagination={false}
            size="small"
            rowKey={(record, index) => record.id || record.isbn || index}
            scroll={{ x: true }}
          />
        </Card>

        {/* New Suppliers Details */}
        {newSuppliers.length > 0 && confirmNewSupplier && (
          <Card title="New Suppliers to be Added" size="small" style={{ marginBottom: 16 }}>
            {newSuppliers.map((supplier, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="Name">{supplier.name}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{supplier.phone || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>{supplier.address || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </div>
            ))}
          </Card>
        )}

        {/* New Products Details */}
        {newProducts.length > 0 && selectedProducts.length > 0 && (
          <Card title="New Products to be Added" size="small" style={{ marginBottom: 16 }}>
            <Table
              columns={[
                { title: 'ISBN', dataIndex: 'isbn', key: 'isbn' },
                { title: 'Title', dataIndex: 'title', key: 'title' },
                { title: 'Author', dataIndex: 'author', key: 'author' },
                { title: 'Publisher', dataIndex: 'publisher', key: 'publisher' },
                { title: 'Currency', dataIndex: 'currency', key: 'currency' }
              ]}
              dataSource={newProducts}
              pagination={false}
              size="small"
              rowKey={(record, index) => record.isbn || index}
            />
          </Card>
        )}

        {/* Financial Summary */}
        <Card title="Financial Summary" size="small">
          <div style={{ textAlign: 'right' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Subtotal:</Text>
                <Text strong>₹{totals.subtotal.toFixed(2)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Discount:</Text>
                <Text strong>₹{totals.discount.toFixed(2)}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Total Amount:</Text>
                <Text strong style={{ fontSize: '16px' }}>₹{totals.total.toFixed(2)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Paid Amount:</Text>
                <Text strong>₹{(purchaseData?.paid_amount || 0).toFixed(2)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Due Amount:</Text>
                <Text strong style={{ color: totals.total - (purchaseData?.paid_amount || 0) > 0 ? '#ff4d4f' : '#52c41a' }}>
                  ₹{(totals.total - (purchaseData?.paid_amount || 0)).toFixed(2)}
                </Text>
              </div>
            </Space>
          </div>
        </Card>
      </div>
    </Modal>
  );
};

export default PurchaseConfirmation;
